import { submitFormJson } from 'widgets/toolbox/ajax';
import { timeout } from 'widgets/toolbox/util';
import { setCurrentToken } from '../toolbox/ajax';

/**
 * @typedef {ReturnType <typeof import('widgets/global/Modal').default>} Modal
 */

/**
 * @description Base SessionExpiration implementation
 * @param {Modal} Modal Base widget for extending
 * @returns {typeof SessionExpiration} SessionExpiration class
 */
export default function (Modal) {
    /**
     * @category widgets
     * @subcategory global
     * @class SessionExpiration
     * @augments Modal
     * @classdesc Session expiration component. Used to notify customer via popup, that his/her session is about to expire.
     * 1. At page loads starts a background timeout with configured time.
     * 2. After timeout, popup with session expiration alert will be shown. Popup also contains countdown before session will be expired.
     * 3. Any customer interaction with popup will lead to backend ping and CSRF token re-newal, which prolongs customer session.
     * 4. If popup was not closed - redirect to session expiration page happens.
     * @property {string} data-widget - Widget name `sessionExpiration`
     * @property {string} data-disable-rendering - Can disable modal dynamic content rendering.
     * Should be set to `true` in order countdown to work correctly!
     * @property {string} data-session-timeout-url - URL to redirect customer once session timeout
     * @property {string} data-session-continue-url - URL to get updated token and prolong customer's session
     * @property {number} data-initial-timeout - Initial timeout in minutes prior to expiration popup
     * @property {number} data-countdown-timeout - Countdown time in minutes up to session expires
     * @example <caption>Example of Session Expiration DOM structure</caption>
     * <div
     *     data-widget="sessionExpiration"
     *     data-disable-rendering="true"
     *     data-session-timeout-url="${URLUtils.url('CSRF-Fail')}"
     *     data-session-continue-url="${URLUtils.url('CSRF-Generate')}"
     *     data-initial-timeout="24"
     *     data-countdown-timeout="5"
     * >
     *     <div
     *         class="b-dialog"
     *         aria-label=""
     *         data-ref="container"
     *         hidden
     *     >
     *         <div
     *             class="b-dialog-window"
     *             role="dialog"
     *             data-ref="dialog"
     *             aria-modal="true"
     *         >
     *             <div class="b-dialog-header">
     *                 <isinclude template="components/modal/closeButton">
     *             </div>
     *             <div class="b-dialog-body b-user_content">
     *                 <isprint value="${Resource.msg('sessionExpiration.abouttoexpire', 'global', null)}" />
     *                 <div data-ref="countdown"></div>
     *                 <svg class="b-countdown-icon" data-ref="countdownCircle">
     *                     <circle data-ref="countdownCircleBg" class="b-countdown-circle_bg" r="92" cx="95" cy="95"></circle>
     *                     <circle data-ref="countdownCircleAnimated" class="b-countdown-circle_animated" r="92" cx="95" cy="95"></circle>
     *                 </svg>
     *                 <button
     *                     type="button"
     *                     class="b-button"
     *                     data-event-click.prevent="continueSession"
     *                     data-dismiss="modal"
     *                     data-ref="lastFocusElement"
     *                 >
     *                     <isprint value="${Resource.msg('sessionExpiration.continuesession', 'global', null)}" />
     *                 </button>
     *             </div>
     *         </div>
     *     </div>
     * </div>
     */
    class SessionExpiration extends Modal {
        prefs() {
            return {
                initialTimeout: 24,
                countdownTimeout: 5,
                countdown: 'countdown',
                countdownCircleAnimated: 'countdownCircleAnimated',
                countdownIcon: 'countdownIcon',
                countdownCircleBg: 'countdownCircleBg',
                sessionTimeoutUrl: '',
                sessionContinueUrl: '',
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization logic
         * @listens module:ajax#responseok
         * @listens dom#focus
         * @returns {void}
         */
        init() {
            super.init();
            this.onDestroy(() => this.clearDisposableResources());
            this.eventBus().on('responseok', 'onOKResponse');
            this.resetInitialTimeout();
            this.started = Date.now();

            this.focusListener = this.ev('focus', () => this.handlePageFocus(), window);
            this.onDestroy(() => {
                if (this.focusListener) {
                    this.focusListener.forEach((disposable) => disposable());
                    this.focusListener = undefined;
                }
            });
        }

        /**
         * @description Evaluates actions needed after success AJAX responce
         * @returns {void}
         */
        onOKResponse() {
            this.started = Date.now();
            this.resetInitialTimeout();
        }

        /**
         * @description Method needed to properly handle mobile/tablet devices either
         * after application switch or after device turn on after sleep mode, because mobile/tablet
         * platforms will suspend `setTimeout`/`setInterval` operations, when tab is in background or device is in sleep mode
         * Methos adjusts timings in comparison to last active time.
         * @returns {void}
         */
        handlePageFocus() {
            if (!this.started) {
                return;
            }

            const pageFocused = Date.now();
            const timePassed = (pageFocused - this.started) / 1000 / 60;

            const passedMoreThanInitialTimeout = timePassed > this.prefs().initialTimeout;
            const passedMoreThanCountdown = timePassed > (this.prefs().initialTimeout + this.prefs().countdownTimeout);

            if (passedMoreThanCountdown) {
                this.sessionExpired();
                return;
            }

            if (passedMoreThanInitialTimeout && !passedMoreThanCountdown) {
                // adjust countdown
                if (!this.countdownShown) {
                    this.startCountdown().then(() => this.adjustCountdown(timePassed));
                } else {
                    this.adjustCountdown(timePassed);
                }

                return;
            }

            // adjust initial timeout
            this.resetInitialTimeout((this.prefs().initialTimeout - timePassed) * 60 * 1000);
        }

        /**
         * @description Executes started countdown adjustment
         * @param {number} actualTime - actual time for countdown
         * @returns {void}
         */
        adjustCountdown(actualTime) {
            const countdownAdjust = (this.prefs().initialTimeout + this.prefs().countdownTimeout) - actualTime;
            this.clearDisposableResources();
            this.initCountdown(countdownAdjust * 60);
        }

        /**
         * @description Resets initial timeout and starts new timeout. Usually is called once any ajax request finishes
         * @param {number|undefined} actualTimeout - actual time for initial timeout
         * @returns {void}
         */
        resetInitialTimeout(actualTimeout = undefined) {
            this.clearDisposableResources();
            this.initialTimeout = timeout(() => {
                this.startCountdown();
            }, actualTimeout || (this.prefs().initialTimeout * 60 * 1000));
        }

        /**
         * @description Shows "Session expires in..." modal and starts countdown.
         * Once countdown finishes, trigger session expiration logic
         * @returns {Promise} result
         */
        startCountdown() {
            this.showModal();
            this.countdownShown = true;
            this.ref('countdown').setText(`${this.prefs().countdownTimeout}:00`);
            const countdownCircle = this.ref('countdownCircle').get();
            const countdownCircleBg = this.ref('countdownCircleBg').get();
            const countdownCircleAnimated = this.ref('countdownCircleAnimated').get();
            if (countdownCircle && countdownCircleAnimated && countdownCircleBg) {
                const TAU = Math.PI * 2;
                const radius = 92;
                this.circleLength = TAU * radius;
                countdownCircleBg.setAttributeNS(null, 'r', String(radius));
                countdownCircleAnimated.setAttributeNS(null, 'r', String(radius));
            }

            return this.initCountdown();
        }

        /**
         * @description Initiates countdown with predefined starting time
         * @param {number|undefined} time - time to start countdown
         * @returns {Promise} result
         */
        initCountdown(time = undefined) {
            const countdownCircleAnimated = this.ref('countdownCircleAnimated').get();
            const circleLength = this.circleLength || 0;
            const countdownTimeoutSeconds = this.prefs().countdownTimeout * 60;

            return import(/* webpackChunkName: 'simplecountdown' */'widgets/toolbox/SimpleCountdown').then((SimpleCountdown) => {
                // eslint-disable-next-line new-cap
                this.countdown = new SimpleCountdown.default(progress => {
                    this.ref('countdown').setText(`${progress.minutes}:${progress.seconds}`);

                    const passedTimePercent = 1 - (progress.duration / countdownTimeoutSeconds);
                    const visibleCircleLength = circleLength - Math.round(circleLength * passedTimePercent);

                    if (countdownCircleAnimated) {
                        countdownCircleAnimated.setAttributeNS(null, 'stroke-dasharray',
                            (visibleCircleLength + ',' + circleLength));
                    }
                }, () => this.sessionExpired(), {
                    time: time || this.prefs().countdownTimeout * 60
                });
            });
        }

        /**
         * @description Session expired logic. Redirects customer to given URL.
         * @returns {void}
         */
        sessionExpired() {
            this.clearDisposableResources();
            window.location.assign(this.prefs().sessionTimeoutUrl);
        }

        /**
         * @description Continue session logic. Sends request to update csrf-token.
         * @returns {Promise} result
         */
        continueSession() {
            return submitFormJson(this.prefs().sessionContinueUrl, {}).then((response) => {
                setCurrentToken(response.csrf.tokenName, response.csrf.token);
                this.closeModal();
                this.countdownShown = false;
            });
        }

        /**
         * @description Method, which is called once customer closes modal
         * (i.e. press close button or clicked outside modal)
         * @returns {void}
         */
        cancel() {
            this.continueSession().finally(() => {
                super.cancel();
            });
        }

        /**
         * @description Clear disposable resources
         * @returns {void}
         */
        clearDisposableResources() {
            if (this.initialTimeout) {
                this.initialTimeout();
                this.initialTimeout = undefined;
            }
            if (this.countdown) {
                this.countdown.destroy();
                this.countdown = undefined;
            }
        }
    }

    return SessionExpiration;
}
